import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList,
  MapPin,
  Plus,
  Search,
  Users,
  UserRoundCog,
  Waves,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Textarea } from "../components/Textarea";
import { Field } from "../components/Field";

export default function LakesPage() {
  const queryClient = useQueryClient();
  const {
    data: lakes = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["carpintel_lakes"],
    queryFn: () => fetch("/app-api/lakes").then((r) => r.json()),
  });

  // Admin visibility: server is the real gate; this only controls UI visibility.
  const { data: adminData } = useQuery({
    queryKey: ["admin_me"],
    queryFn: () =>
      fetch("/app-api/admin/me")
        .then((r) => (r.ok ? r.json() : { isAdmin: false }))
        .catch(() => ({ isAdmin: false })),
    staleTime: 60_000,
  });
  const isAdmin = adminData?.isAdmin === true;
  const [form, setForm] = useState({
    name: "",
    location: "",
    latitude: "",
    longitude: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  async function addLake(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    const response = await fetch("/app-api/lakes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        latitude: form.latitude || null,
        longitude: form.longitude || null,
      }),
    });
    const result = await response.json();
    setSaving(false);
    if (!response.ok) {
      setMessage(result.error || "Could not save lake");
      return;
    }
    setForm({ name: "", location: "", latitude: "", longitude: "", notes: "" });
    setMessage("Lake added");
    queryClient.invalidateQueries({ queryKey: ["carpintel_lakes"] });
  }

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header className="flex min-w-0 items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-secondary">Places to fish</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-primary">
            My lakes
          </h1>
        </div>
        <Button asChild size="lg">
          <Link to="/lakes/find">
            <Search /> Find a lake
          </Link>
        </Button>
      </header>

      {/* Find a lake CTA */}
      <Button asChild size="xl" className="w-full justify-start">
        <Link to="/lakes/find">
          <Search />
          <span className="text-left">
            Find a lake — search 100+ curated waters
          </span>
        </Link>
      </Button>

      {/* My Memberships entry */}
      <Button
        asChild
        variant="secondary"
        size="xl"
        className="w-full justify-start"
      >
        <Link to="/lakes/memberships">
          <Users />
          <span className="text-left">My memberships</span>
        </Link>
      </Button>

      {/* Admin-only controls — server remains the real gate */}
      {isAdmin ? (
        <div className="grid gap-3 md:grid-cols-2">
          <Button
            asChild
            variant="secondary"
            size="xl"
            className="w-full justify-start"
          >
            <Link to="/admin/accounts">
              <UserRoundCog />
              <span className="text-left">Account Access (Admin)</span>
            </Link>
          </Button>
          <Button
            asChild
            variant="secondary"
            size="xl"
            className="w-full justify-start"
          >
            <Link to="/admin/lake-reviews">
              <ClipboardList />
              <span className="text-left">Lake Reviews (Admin)</span>
            </Link>
          </Button>
        </div>
      ) : null}

      <section
        id="add-lake"
        className="rounded-lg border border-border bg-raised p-4"
      >
        <h2 className="text-lg font-semibold text-primary">
          Add a private lake manually
        </h2>
        <form onSubmit={addLake} className="mt-4 space-y-4">
          <Field label="Lake name" id="lake-name">
            <Input
              id="lake-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Willowmere Lake"
              required
            />
          </Field>
          <Field label="Location" id="lake-location">
            <Input
              id="lake-location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Town or county"
            />
          </Field>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label="Latitude"
              id="lake-latitude"
              help="Optional coordinates for future automatic weather lookup."
            >
              <Input
                id="lake-latitude"
                type="number"
                step="0.000001"
                value={form.latitude}
                onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                placeholder="51.7520"
              />
            </Field>
            <Field
              label="Longitude"
              id="lake-longitude"
              help="Optional coordinates for future automatic weather lookup."
            >
              <Input
                id="lake-longitude"
                type="number"
                step="0.000001"
                value={form.longitude}
                onChange={(e) =>
                  setForm({ ...form, longitude: e.target.value })
                }
                placeholder="-1.2577"
              />
            </Field>
          </div>
          <Field label="Notes" id="lake-notes">
            <Textarea
              id="lake-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Useful pegs, features or access notes"
              rows={4}
            />
          </Field>
          <div className="flex items-center gap-3">
            <Button type="submit" isLoading={saving}>
              <Plus /> Add lake
            </Button>
            {message ? (
              <p className="text-sm text-secondary" role="status">
                {message}
              </p>
            ) : null}
          </div>
        </form>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-primary">Saved lakes</h2>
        {isLoading ? (
          <Loading />
        ) : error ? (
          <ErrorState onRetry={() => refetch()} />
        ) : lakes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center">
            <Waves className="mx-auto text-secondary" />
            <p className="mt-3 text-sm text-secondary">
              No lakes yet. Add your first water above.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {lakes.map((lake: any) => (
              <Link
                key={lake.id}
                to={`/lakes/${lake.id}`}
                className="block min-w-0 rounded-lg border border-border bg-raised p-4 transition-colors hover:bg-inset"
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-primary">
                      {lake.name}
                    </h3>
                    <p className="mt-1 flex min-w-0 items-center gap-1 truncate text-sm text-secondary">
                      <MapPin className="size-4 shrink-0" />
                      {lake.location || "Location not recorded"}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-inset px-2 py-0.5 text-xs font-medium text-secondary">
                    {lake.catch_count}{" "}
                    {Number(lake.catch_count) === 1 ? "catch" : "catches"}
                  </span>
                </div>
                <p className="mt-3 line-clamp-2 text-sm text-secondary">
                  {lake.notes || "No notes yet."}
                </p>
                <p className="mt-3 text-xs text-secondary">
                  {lake.session_count}{" "}
                  {Number(lake.session_count) === 1 ? "session" : "sessions"}
                </p>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Loading() {
  return (
    <div className="rounded-lg border border-border bg-raised p-6 text-center text-sm text-secondary">
      Loading lakes…
    </div>
  );
}
function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-lg border border-error bg-error-weak p-4 text-sm text-error">
      Could not load lakes.{" "}
      <Button variant="tertiary" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
